// School-admin / teacher → Students. Add a student (username availability +
// password + optional class), list, reset password, remove. Mirrors the parent
// Children panel. docs/replan-v2.md R9.

import { useT } from '@fajrarabi/i18n/react';
import { Alert, Button, Card, CardBody, Field, Input, Skeleton } from '@fajrarabi/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

import { ApiError } from '~/lib/api';
import { checkUsername } from '~/lib/family';
import {
  createStudent,
  deleteStudent,
  listClasses,
  listStudents,
  resetStudentPassword,
  type Grade,
  type Student,
} from '~/lib/school';

const GRADES: Grade[] = ['kg2', 'g1', 'g2', 'g3', 'g4', 'g5', 'g6'];
type Availability = { state: 'idle' | 'checking' | 'available' | 'taken'; suggestions: string[] };

export const Route = createFileRoute('/$locale/school/students')({
  component: StudentsScreen,
});

function StudentsScreen() {
  const t = useT();
  const qc = useQueryClient();
  const students = useQuery({
    queryKey: ['school', 'students'],
    queryFn: ({ signal }) => listStudents(signal),
  });
  const classes = useQuery({
    queryKey: ['school', 'classes'],
    queryFn: ({ signal }) => listClasses(signal),
  });

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [grade, setGrade] = useState<Grade>('kg2');
  const [classId, setClassId] = useState('');
  const [availability, setAvailability] = useState<Availability>({
    state: 'idle',
    suggestions: [],
  });

  // Debounced username availability.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const candidate = username.trim().toLowerCase();
    if (candidate.length < 3) {
      setAvailability({ state: 'idle', suggestions: [] });
      return;
    }
    setAvailability({ state: 'checking', suggestions: [] });
    debounceRef.current = setTimeout(() => {
      void checkUsername(candidate)
        .then((res) =>
          setAvailability(
            res.available
              ? { state: 'available', suggestions: [] }
              : { state: 'taken', suggestions: res.suggestions },
          ),
        )
        .catch(() => setAvailability({ state: 'idle', suggestions: [] }));
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [username]);

  const create = useMutation({
    mutationFn: () =>
      createStudent({
        username: username.trim().toLowerCase(),
        password,
        displayName: name.trim(),
        grade,
        classId: classId || null,
      }),
    onSuccess: async () => {
      setUsername('');
      setPassword('');
      setName('');
      setClassId('');
      setAvailability({ state: 'idle', suggestions: [] });
      await qc.invalidateQueries({ queryKey: ['school', 'students'] });
      await qc.invalidateQueries({ queryKey: ['school', 'classes'] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteStudent(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['school', 'students'] });
    },
  });

  return (
    <div className="mx-auto max-w-3xl px-6 py-10">
      <h1 className="text-2xl font-bold text-ink-900">{t('app.school.students_title')}</h1>

      <Card className="mt-6">
        <CardBody>
          {create.isError && (
            <Alert tone="danger" className="mb-4">
              {create.error instanceof ApiError
                ? create.error.message
                : t('app.common.error_generic')}
            </Alert>
          )}
          <form
            className="grid gap-4 sm:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate();
            }}
          >
            <Field label={t('app.school.student_name')}>
              {(id) => (
                <Input id={id} value={name} onChange={(e) => setName(e.target.value)} required />
              )}
            </Field>
            <div>
              <Field label={t('app.school.student_username')}>
                {(id) => (
                  <Input
                    id={id}
                    autoCapitalize="none"
                    spellCheck={false}
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                  />
                )}
              </Field>
              {availability.state === 'available' && (
                <p className="mt-1 text-xs font-semibold text-leaf">
                  ✓ {t('app.school.username_free')}
                </p>
              )}
              {availability.state === 'taken' && (
                <div className="mt-1 text-xs text-ink-700">
                  <p className="font-semibold text-heart">{t('app.school.username_taken')}</p>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {availability.suggestions.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setUsername(s)}
                        className="rounded-full bg-cream-100 px-2.5 py-1 font-medium text-burgundy-500 ring-1 ring-burgundy-500/10 hover:bg-cream-200"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <Field label={t('app.school.student_password')}>
              {(id) => (
                <Input
                  id={id}
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              )}
            </Field>
            <Field label={t('app.school.grade')}>
              {(id) => (
                <select
                  id={id}
                  value={grade}
                  onChange={(e) => setGrade(e.target.value as Grade)}
                  className="h-11 w-full rounded-xl border border-ink-500/20 bg-white px-3 text-ink-900"
                >
                  {GRADES.map((g) => (
                    <option key={g} value={g}>
                      {g.toUpperCase()}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <Field label={t('app.school.add_to_class')}>
              {(id) => (
                <select
                  id={id}
                  value={classId}
                  onChange={(e) => setClassId(e.target.value)}
                  className="h-11 w-full rounded-xl border border-ink-500/20 bg-white px-3 text-ink-900"
                >
                  <option value="">{t('app.school.no_class_option')}</option>
                  {(classes.data ?? []).map((cls) => (
                    <option key={cls.id} value={cls.id}>
                      {cls.name}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <div className="flex items-end">
              <Button type="submit" disabled={create.isPending}>
                {t('app.school.add_student')}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>

      <div className="mt-8 space-y-3">
        {students.isLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : (students.data?.length ?? 0) === 0 ? (
          <p className="text-sm text-ink-700">{t('app.school.no_students')}</p>
        ) : (
          students.data!.map((s) => (
            <StudentRow
              key={s.id}
              student={s}
              onReset={(pw) => resetStudentPassword(s.id, pw)}
              onRemove={() => remove.mutate(s.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function StudentRow({
  student,
  onReset,
  onRemove,
}: {
  student: Student;
  onReset: (password: string) => Promise<void>;
  onRemove: () => void;
}) {
  const t = useT();
  const [resetting, setResetting] = useState(false);
  const [pw, setPw] = useState('');
  const reset = useMutation({
    mutationFn: () => onReset(pw),
    onSuccess: () => {
      setResetting(false);
      setPw('');
    },
  });

  return (
    <div className="rounded-2xl bg-white p-4 shadow-[0_12px_30px_-22px_rgba(31,26,23,0.25)] ring-1 ring-burgundy-500/10">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-semibold text-ink-900">{student.displayName ?? student.username}</p>
          <p className="text-sm text-ink-500">@{student.username}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setResetting((v) => !v)}>
            {t('app.school.reset_password')}
          </Button>
          <Button variant="ghost" size="sm" onClick={onRemove}>
            {t('app.school.remove')}
          </Button>
        </div>
      </div>
      {resetting && (
        <form
          className="mt-3 flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            reset.mutate();
          }}
        >
          <Field label={t('app.school.student_password')} className="flex-1">
            {(id) => (
              <Input
                id={id}
                type="password"
                autoComplete="new-password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                required
              />
            )}
          </Field>
          <Button type="submit" size="sm" disabled={reset.isPending}>
            {t('app.common.save')}
          </Button>
        </form>
      )}
    </div>
  );
}
